'use client';

import { PropsWithChildren } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { OperationType, SpacePlanPaymentInfo, SubscribePlan } from '../../../../../types';
import { useManagePlanPaymentFormEffects } from '../../hooks';
import { OnetimePeriod, PlanTemplateType, SubscriptionPeriod } from '../../types';
import { convertStartOfDayISOString } from '../../utils';
export const PlanPaymentFormProvider = ({
  planType,
  maxCcu,
  planTemplateType,
  subscriptionPeriod,
  onetimePeriod,
  onetimeStartDate,
  planPaymentInfo,
  children
}: PropsWithChildren<PlanPaymentFormProps>) => {
  const is정기결제사용중 = planPaymentInfo?.spacePlan?.status === 'ACTIVE' && planPaymentInfo?.spacePlan?.operationType === OperationType.정기;
  const is일시납결제사용중 = planPaymentInfo?.spacePlan?.status === 'ACTIVE' && planPaymentInfo?.spacePlan?.operationType === OperationType.일시납;
  const initPlanType = planPaymentInfo?.spacePlan?.status === 'ACTIVE' ? planPaymentInfo?.spacePlan?.planType.toLowerCase() : planType;
  const methods = useForm<PlanPaymentFormType>({
    resolver: zodResolver(PlanPaymentFormSchema),
    defaultValues: {
      planType: initPlanType as SubscribePlan,
      maxCcu,
      planTemplateType: is정기결제사용중 ? PlanTemplateType.PRODUCT_SUBSCRIPTION : is일시납결제사용중 ? PlanTemplateType.PRODUCT_ONETIME : planTemplateType,
      subscriptionPeriod: is정기결제사용중 && planPaymentInfo.subscription?.subscriptionPeriod === 365 ? '365' : subscriptionPeriod,
      onetimePeriod: is일시납결제사용중 && planPaymentInfo?.spacePlan?.onetimePeriod ? planPaymentInfo?.spacePlan?.onetimePeriod as OnetimePeriod : onetimePeriod,
      isCardRegistered: !!planPaymentInfo?.billingInfo,
      isCustomOnetimePeriod: is일시납결제사용중 && planPaymentInfo?.spacePlan?.onetimePeriod ? (planPaymentInfo?.spacePlan?.onetimePeriod as OnetimePeriod)?.includes('M') : onetimePeriod?.includes('M'),
      isTermsAgreed: false,
      onetimeStartDate: is일시납결제사용중 ? new Date(convertStartOfDayISOString(planPaymentInfo?.spacePlan?.startDate)) : onetimeStartDate ?? new Date(),
      // 초기값으로 해외 정기 결제에서만 사용됩니다.
      zipCode: planPaymentInfo?.billingInfo?.zipCode || ''
    }
  });
  useManagePlanPaymentFormEffects(methods, planPaymentInfo);
  return <FormProvider {...methods} data-sentry-element="FormProvider" data-sentry-component="PlanPaymentFormProvider" data-sentry-source-file="PlanPaymentFormProvider.tsx">{children}</FormProvider>;
};
export const usePlanPaymentFormContext = () => {
  // Caveats: 가장 가까운 FormProvider를 찾아 context를 반환합니다.
  const context = useFormContext<PlanPaymentFormType>();
  if (!context) {
    throw new Error('usePlanPaymentFormContext must be used within a PlanPaymentFormProvider');
  }
  return context;
};
export interface PlanPaymentFormProps {
  planType: 'free' | 'pro' | 'basic' | 'enterprise';
  maxCcu: number;
  planTemplateType: PlanTemplateType;
  subscriptionPeriod?: SubscriptionPeriod;
  onetimePeriod?: OnetimePeriod;
  /** 쿼리로 지정된 일시납 시작일. 없으면 오늘. 견적서의 `결제 바로가기` 가 쓴다. */
  onetimeStartDate?: Date;
  planPaymentInfo?: SpacePlanPaymentInfo;
}
const PlanPaymentFormSchema = z.object({
  planType: z.enum(['free', 'pro', 'basic', 'enterprise']),
  // Omit<SubscribePlan, 'free'> assumed to be a union of string literals
  maxCcu: z.number().min(10, 'CCU must be at least 10'),
  // Assuming positive numbers only
  planTemplateType: z.nativeEnum(PlanTemplateType),
  // PlanTemplateType as enum
  subscriptionPeriod: z.union([z.literal('30'), z.literal('365')]).optional(),
  onetimePeriod: z.union([z.literal('1D'), z.literal('3D'), z.literal('1M'), z.literal('2M'), z.literal('3M'), z.literal('4M'), z.literal('5M'), z.literal('6M'), z.literal('7M'), z.literal('8M'), z.literal('9M'), z.literal('10M'), z.literal('11M'), z.literal('12M')]).optional(),
  isCustomOnetimePeriod: z.boolean(),
  onetimeStartDate: z.date(),
  isCardRegistered: z.boolean().refine(val => val, {
    message: '카드를 등록해야 결제할 수 있습니다.'
  }),
  isTermsAgreed: z.boolean().refine(val => val, {
    message: '약관에 동의해야 합니다.'
  }),
  zipCode: z.string().optional()
});
export type PlanPaymentFormType = z.infer<typeof PlanPaymentFormSchema>;